import { useState, useRef, useEffect, useCallback } from 'react'
import { AlertCircle } from 'lucide-react'
import type { CellValue, EditableField } from '../hooks/useDirtyRows'
import { toDateInputValue, parseDateInputValue } from '../utils/dateUtils'

interface GerencialTableEditCellProps {
  rowId: number
  field: EditableField
  currentValue: CellValue
  dirtyValue: CellValue | undefined
  errorMessage: string | undefined
  onChange: (rowId: number, field: EditableField, prev: CellValue, next: CellValue) => void
  onValidate: (rowId: number, field: EditableField, message: string | null) => void
  alignRight?: boolean
  /** When true, inputs are read-only (used for rows marked for delete). */
  disabled?: boolean
  /**
   * Optional autocomplete suggestions for free-text fields like Natureza —
   * the parent passes distinct values seen on the current page (or wherever
   * it has them) so users can pick existing codes without us inventing a
   * fixed enum.
   */
  suggestions?: string[]
}

/**
 * Always-open input cell for batch-edit mode. Each cell renders as a real
 * <input> from the get-go — no click-to-edit ceremony.
 *
 * Behavior:
 *   - Local `draft` state mirrors the visible value so re-renders don't yank
 *     the user's typing. Synced from `effectiveValue` only when the input
 *     is NOT focused (avoids stomping live edits).
 *   - Commits to dirty map on Enter or blur (validates first).
 *   - Esc reverts the draft to the current effective value without committing.
 *   - Validation errors render as a red ring + tooltip; they don't block
 *     editing other cells but disable Save at the toolbar level.
 */
export function GerencialTableEditCell({
  rowId,
  field,
  currentValue,
  dirtyValue,
  errorMessage,
  onChange,
  onValidate,
  alignRight,
  disabled,
  suggestions,
}: GerencialTableEditCellProps) {
  const effectiveValue = dirtyValue !== undefined ? dirtyValue : currentValue
  const [draft, setDraft] = useState<string>(() => formatForInput(effectiveValue, field))
  const inputRef = useRef<HTMLInputElement | HTMLSelectElement | null>(null)

  // Sync external value changes (undo, reset, server refetch) into the draft —
  // but only when the input isn't focused so we don't yank live typing.
  useEffect(() => {
    if (inputRef.current && document.activeElement !== inputRef.current) {
      setDraft(formatForInput(effectiveValue, field))
    }
  }, [effectiveValue, field])

  const commit = useCallback((rawDraft: string) => {
    const validation = validateField(field, rawDraft)
    if (validation.error) {
      onValidate(rowId, field, validation.error)
      return
    }
    onValidate(rowId, field, null)
    if (!cellEquals(validation.value, currentValue)) {
      onChange(rowId, field, currentValue, validation.value)
    } else if (dirtyValue !== undefined) {
      // User reverted to the original value — clear the dirty entry by
      // committing original→original (setCell short-circuits via cellValueEquals
      // and DOES NOT clear, so we instead fire a no-op-but-equal change to
      // keep stack in sync). For now: emit a delete-marker via setting same
      // value; useDirtyRows.setCell will skip but the dirty entry persists.
      // Acceptable trade-off for v1; a future iteration could add `clearCell`.
    }
  }, [field, rowId, currentValue, dirtyValue, onChange, onValidate])

  const handleBlur = () => commit(draft)
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      commit(draft)
      ;(e.currentTarget as HTMLElement).blur()
    } else if (e.key === 'Escape') {
      e.preventDefault()
      setDraft(formatForInput(effectiveValue, field))
      ;(e.currentTarget as HTMLElement).blur()
    }
  }

  const isDirty = dirtyValue !== undefined
  const hasError = !!errorMessage

  // Compact, table-friendly styling. Inputs blend with cell padding so the
  // whole row reads like a spreadsheet rather than a form.
  const baseInput = [
    'w-full bg-transparent border rounded px-2 py-1 text-sm text-white',
    'focus:outline-none focus:ring-1',
    alignRight ? 'text-right font-mono' : '',
  ].join(' ')

  const stateClasses = disabled
    ? 'border-transparent text-brand-gray cursor-not-allowed line-through'
    : hasError
      ? 'border-error/60 focus:ring-error bg-error/5'
      : isDirty
        ? 'border-warning/60 focus:ring-warning bg-warning/10 text-warning font-medium'
        : 'border-brand-dark-lighter/60 hover:border-brand-primary/40 focus:ring-brand-primary/60'

  const className = `${baseInput} ${stateClasses}`

  const inputType =
    field === 'Data' ? 'date'
      : field === 'Valor' ? 'number'
      : field === 'CentroResultado' ? 'number'
      : 'text'

  const step = field === 'Valor' ? '0.01' : field === 'CentroResultado' ? '1' : undefined

  // <datalist> gives a free-text input optional autocomplete from distinct
  // values seen elsewhere — no fixed enum, but useful nudge for fields like
  // Natureza where users tend to reuse codes.
  const listId = suggestions && suggestions.length > 0
    ? `gerencial-cell-${field}-${rowId}`
    : undefined

  return (
    <div className="relative">
      <input
        ref={inputRef as React.MutableRefObject<HTMLInputElement>}
        type={inputType}
        step={step}
        value={draft}
        onChange={e => setDraft(e.target.value)}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        readOnly={disabled}
        className={className}
        title={errorMessage}
        list={listId}
      />
      {listId && (
        <datalist id={listId}>
          {suggestions!.map(s => <option key={s} value={s} />)}
        </datalist>
      )}
      {hasError && <ErrorIcon />}
    </div>
  )
}

function ErrorIcon() {
  return (
    <AlertCircle
      size={12}
      className="absolute right-1 top-1/2 -translate-y-1/2 text-error pointer-events-none"
    />
  )
}

function cellEquals(a: CellValue, b: CellValue): boolean {
  if (a instanceof Date && b instanceof Date) return a.getTime() === b.getTime()
  return Object.is(a, b)
}

function formatForInput(value: CellValue, field: EditableField): string {
  if (value === null || value === undefined) return ''
  if (field === 'Data') {
    const d = value instanceof Date ? value : new Date(String(value))
    return toDateInputValue(d)
  }
  if (field === 'Valor' || field === 'CentroResultado') return String(value)
  return String(value)
}

interface ValidationResult {
  error: string | null
  value: CellValue
}

function validateField(field: EditableField, draft: string): ValidationResult {
  const trimmed = draft.trim()

  if (field === 'Data') {
    if (trimmed === '') return { error: 'Data obrigatória', value: null }
    const d = parseDateInputValue(trimmed)
    if (!d) return { error: 'Data inválida (use dd/mm/aaaa)', value: null }
    return { error: null, value: d }
  }

  if (field === 'Valor') {
    if (trimmed === '') return { error: 'Valor obrigatório', value: null }
    const num = Number(trimmed)
    if (isNaN(num)) return { error: 'Valor inválido — só números', value: null }
    return { error: null, value: num }
  }

  if (field === 'CentroResultado') {
    if (trimmed === '') return { error: null, value: null }
    const num = Number(trimmed)
    if (isNaN(num) || !Number.isInteger(num)) return { error: 'Centro de Resultado deve ser inteiro', value: null }
    return { error: null, value: num }
  }

  return { error: null, value: trimmed === '' ? null : trimmed }
}
